import * as Sentry from '@sentry/vue'
import CrossDomainHttpClient from '@/services/crossDomainHttp'
import Configs from '@/config'
import PostMessageSender from '@/services/postMessageSender'
import AIChatbotReadinessService from '@/services/aiChatbotReadiness'
import EventBus from '@/eventBus'
import utils from '@/util'
import router from '@/router'
import i18nPresenter from '@/i18nPresenter'
import {
  IMPORT_OPTIONS, IMPORT_ACTION_OPTIONS, UPLOAD_FILE_SUCCESSFUL,
  UPLOAD_FILE_FAILED, IMPORT_V2_SUCCESSFUL, IMPORT_V2_FAILED,
} from '@/constant/eventActions/ImportEventActions'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

const { externalApis } = Configs
const IFRAME_CONTENT_MAX_WAIT_TIME = 20000
const IFRAME_POST_READINESS_DELAY_MS = 2000
const IS_FEATURE_ENABLED = 'featureToggles/isFeatureEnabled'

/**
 * Generates authentication headers for API requests
 * @returns {Object} Headers object with Authorization token if available
 */
const getAuthHeaders = () => {
  // Simple cookie getter for ahaToken
  const getCookie = (name) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    return parts.length === 2 ? parts.pop().split(';').shift() : null
  }

  const token = getCookie('ahaToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const trackUploadFileSuccessful = (file) => {
  const eventAction = UPLOAD_FILE_SUCCESSFUL
  const trackData = {
    eventAction,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
    file_name: file?.name,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackUploadFileFailed = (file, error) => {
  const eventAction = UPLOAD_FILE_FAILED
  const trackData = {
    eventAction,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
    file_name: file?.name,
    error_message: error?.body?.message || error?.message || 'Could not upload this file. Please try again.',
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const getBaseTrackingData = (fileInfo) => ({
  eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router?.currentRoute?.name],
  file_name: fileInfo?.file_name,
  file_type: fileInfo?.file_type,
  file_metadata: fileInfo?.file_metadata,
  import_option: fileInfo?.import_option,
})

const trackImportSuccessful = (fileInfo) => {
  const eventAction = IMPORT_V2_SUCCESSFUL
  const trackData = {
    eventAction,
    ...getBaseTrackingData(fileInfo),
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackImportFailed = (fileInfo, error) => {
  const eventAction = IMPORT_V2_FAILED
  const trackData = {
    eventAction,
    ...getBaseTrackingData(fileInfo),
    error_message: error?.body?.message || error?.message || 'Import failed',
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const _state = {
  isAIChatbotImportModalVisible: false,
  isAIChatbotImportModalVisibleV2: false,
  isUploadingFile: false,
  importingSlidesForPresentationId: null,
  generatingSlidesForPresentationId: null,
  aiChatbotImportSlidesError: '',
  aiChatbotUploadedFile: null,
  sourceFile: null,
  selectedFileName: '',
  selectedImportOption: IMPORT_OPTIONS.IMPORT_AND_GENERATE,
}

const _getters = {
  canUseAIChatbotImportPptxPdf: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-ai-chatbot-import-pptx-pdf')
  },
  canUseAIChatbotImportUIUX: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-ai-chatbot-import-ui-ux')
  },
  canUseAIChatbotImportGenerateSlides: (_, __, ___, rootGetters) => {
    return rootGetters[IS_FEATURE_ENABLED]('release-ai-chatbot-import-generate-slides')
  },
  canUseAIImportFeature: (_, getters) => {
    return (getters.canUseAIChatbotImportPptxPdf || getters.canUseAIChatbotImportUIUX) &&
      getters.canUseAIChatbotImportGenerateSlides
  },
  isAIChatbotImportModalVisible: (state) => state.isAIChatbotImportModalVisible,
  isAIChatbotImportModalVisibleV2: (state) => state.isAIChatbotImportModalVisibleV2,
  isUploadingFile: (state) => state.isUploadingFile,
  importingSlidesForPresentationId: (state) => state.importingSlidesForPresentationId,
  generatingSlidesForPresentationId: (state) => state.generatingSlidesForPresentationId,

  isCurrentPresentationImportingSlides: (_, __, rootState, rootGetters) => {
    const importingId = rootGetters['aiChatbotImportPptxPdf/importingSlidesForPresentationId']
    const currentId = rootState.presentation.id

    // Only show if we have a valid importing ID and it matches current presentation
    return importingId != null && importingId === currentId
  },

  isCurrentPresentationGeneratingSlides: (_, __, rootState, rootGetters) => {
    const generatingId = rootGetters['aiChatbotImportPptxPdf/generatingSlidesForPresentationId']
    const currentId = rootState.presentation.id

    // Only show if we have a valid generating ID and it matches current presentation
    return generatingId != null && generatingId === currentId
  },

  aiChatbotImportSlidesError: (state) => state.aiChatbotImportSlidesError,
  aiChatbotUploadedFile: (state) => state.aiChatbotUploadedFile,
  sourceFile: (state) => state.sourceFile,
  selectedFileName: (state) => state.selectedFileName,
  isUploadingOrImportingSlides: (state, getters) => getters.isCurrentPresentationImportingSlides ||
    state.isUploadingFile,
  selectedImportOption: (state) => state.selectedImportOption,
}

const mutations = {
  SET_IS_AI_IMPORT_MODAL_VISIBLE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.isAIChatbotImportModalVisible = value
  },
  SET_IS_AI_IMPORT_MODAL_VISIBLE_V2(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.isAIChatbotImportModalVisibleV2 = value
  },
  SET_IS_UPLOADING_FILE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.isUploadingFile = value
  },
  SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID(state, presentationId) {
    // eslint-disable-next-line no-param-reassign
    state.importingSlidesForPresentationId = presentationId
  },
  SET_GENERATING_SLIDES_FOR_PRESENTATION_ID(state, presentationId) {
    // eslint-disable-next-line no-param-reassign
    state.generatingSlidesForPresentationId = presentationId
  },
  SET_AI_IMPORT_SLIDES_ERROR(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.aiChatbotImportSlidesError = value
  },
  SET_AI_CHATBOT_UPLOADED_FILE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.aiChatbotUploadedFile = value
  },
  SET_SOURCE_FILE(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.sourceFile = value
  },
  SET_SELECTED_FILE_NAME(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.selectedFileName = value
  },
  SET_SELECTED_IMPORT_OPTION(state, value) {
    // eslint-disable-next-line no-param-reassign
    state.selectedImportOption = value
  },
}

const actions = {
  setIsAIChatbotImportModalVisibleAction({ commit }, value) {
    commit('SET_IS_AI_IMPORT_MODAL_VISIBLE', value)
  },

  setIsAIChatbotImportModalVisibleV2Action({ commit }, value) {
    commit('SET_IS_AI_IMPORT_MODAL_VISIBLE_V2', value)
  },

  setAIChatbotImportSlidesErrorAction({ commit }, value) {
    commit('SET_AI_IMPORT_SLIDES_ERROR', value)
  },

  setIsUploadingFileAction({ commit }, value) {
    commit('SET_IS_UPLOADING_FILE', value)
  },

  setImportingSlidesForPresentationIdAction({ commit }, presentationId) {
    commit('SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID', presentationId)
  },

  setGeneratingSlidesForPresentationIdAction({ commit }, presentationId) {
    commit('SET_GENERATING_SLIDES_FOR_PRESENTATION_ID', presentationId)
  },

  setAIChatbotUploadedFileAction({ commit }, value) {
    commit('SET_AI_CHATBOT_UPLOADED_FILE', value)
  },

  setSourceFileAction({ commit }, file) {
    commit('SET_SOURCE_FILE', file)
  },

  setSelectedFileNameAction({ commit }, fileName) {
    commit('SET_SELECTED_FILE_NAME', fileName)
  },

  setSelectedImportOptionAction({ commit }, value) {
    commit('SET_SELECTED_IMPORT_OPTION', value)
  },

  async uploadFileAction({ commit, dispatch, rootGetters }, file) {
    try {
      commit('SET_IS_UPLOADING_FILE', true)
      commit('SET_SOURCE_FILE', file) // to re-use this in AIChatbotImportModal.vue

      // Create form data
      const formData = new FormData()
      formData.append('file', file)

      // Use cross-domain HTTP client that bypasses interceptors
      const response = await CrossDomainHttpClient.post(externalApis.pptPdfUpload, formData, getAuthHeaders())

      commit('SET_AI_CHATBOT_UPLOADED_FILE', response.body)

      if (!rootGetters['aiChatbotImportPptxPdf/isAIChatbotImportModalVisibleV2']) {
        dispatch('setIsAIChatbotImportModalVisibleV2Action', true)
      }
      trackUploadFileSuccessful(file)
    } catch (error) {
      commit('SET_AI_IMPORT_SLIDES_ERROR', 'Could not upload this file. Please try again.')
      trackUploadFileFailed(file, error)
      Sentry.captureException(error)
    } finally {
      commit('SET_IS_UPLOADING_FILE', false)
    }
  },

  async processToImportSlidesAction({
    rootState, rootGetters, dispatch,
  }) {
    try {
      const selectedImportOption = rootGetters['aiChatbotImportPptxPdf/selectedImportOption']
      const fileHashId = rootGetters['aiChatbotImportPptxPdf/aiChatbotUploadedFile']?.hash_id
      const isImport = IMPORT_ACTION_OPTIONS.find((option) => option?.value === selectedImportOption)?.isImport

      if (rootState.isShowingSlideSearchPopup) {
        dispatch('setSlideSearchPopupVisibilityAction', false, { root: true })
      }

      if (selectedImportOption === IMPORT_OPTIONS.IMPORT_AND_GENERATE ||
        selectedImportOption === IMPORT_OPTIONS.GENERATE_ONLY) {
        dispatch('aiChatbotImportAndGenerateSlidesAction', {
          importOption: i18nPresenter.t(selectedImportOption),
          isImport,
        })
      } else if (selectedImportOption === IMPORT_OPTIONS.IMPORT_ONLY) {
        dispatch('importSlidesAction', {
          presentationId: rootState.presentation?.id,
          fileHashId,
        })
      } else {
        // Handle unknown/invalid import options
        throw new Error(`Unknown import option: ${selectedImportOption}`)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  async aiChatbotImportAndGenerateSlidesAction({ rootGetters, dispatch }, { importOption, isImport }) {
    try {
      const canUseAIChatbotImportGenerateSlides = rootGetters['aiChatbotImportPptxPdf/canUseAIChatbotImportGenerateSlides']
      if (canUseAIChatbotImportGenerateSlides) {
        if (!rootGetters['rightNav/isAITabActive']) {
          dispatch('rightNav/openAIPanelAction', null, { root: true })
        }

        dispatch('sendImportDataToAIChatbotAction', {
          importOption,
          isImport,
        })
      } else {
        dispatch('docToSlideGenerator/docToSlideUploadFileAction', rootGetters['aiChatbotImportPptxPdf/sourceFile'])
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  async importSlidesAction({ commit, state }, { presentationId, fileHashId }) {
    try {
      if (!presentationId || !fileHashId) return

      await CrossDomainHttpClient.post(externalApis.pptPdfImport, {
        presentation_id: presentationId,
        file_hash_id: fileHashId,
      }, getAuthHeaders())
    } catch (error) {
      commit('SET_IMPORTING_SLIDES_FOR_PRESENTATION_ID', null)

      // Track import failure
      trackImportFailed({
        file_name: state.aiChatbotUploadedFile?.file_metadata?.original_filename,
        file_type: state.aiChatbotUploadedFile?.file_metadata?.content_type,
        file_metadata: state.aiChatbotUploadedFile?.file_metadata,
        import_option: state.selectedImportOption,
      }, error)

      EventBus.$emit('import: show_import_status_error')
      Sentry.captureException(error)
    }
  },

  async sendImportDataToAIChatbotAction({
    rootState, rootGetters, commit, dispatch,
  }, { importOption, isImport }) {
    try {
      commit('SET_IS_AI_IMPORT_MODAL_VISIBLE_V2', false)

      if (rootState.isNewPresentation) {
        dispatch('setIsNewPresentationAction', false, { root: true })
      }

      const isIframeReady = await AIChatbotReadinessService.waitForIframeReady(
        rootGetters,
        IFRAME_CONTENT_MAX_WAIT_TIME,
      )

      if (!isIframeReady) {
        throw new Error(
          `Import V2: AI Chatbot iframe failed to become ready within ${IFRAME_CONTENT_MAX_WAIT_TIME}ms. ` +
          'The iframe may not have loaded properly or there may be a network issue.',
        )
      }

      // Add delay to ensure iframe is fully ready
      await utils.sleep(IFRAME_POST_READINESS_DELAY_MS)

      const uploadedFile = rootGetters['aiChatbotImportPptxPdf/aiChatbotUploadedFile']
      PostMessageSender.sendAIImportRequest({
        importOption,
        isImport,
        file: uploadedFile,
      })
      commit('SET_AI_CHATBOT_UPLOADED_FILE', null)
      commit('SET_SOURCE_FILE', null)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
}

// Export tracking function for use in other modules
export { trackImportSuccessful }
